import { useState } from "react";
import { ActionModal, FIELD_LABEL_CLASS, INPUT_CLASS } from "./ActionModal";
import { RECORD_BY_HAND_PROMPT } from "../../vocabulary";
import type { FixStepAction } from "../../types";

/** The paired-note prompt for issue / complete / skip on a fix-plan step —
 *  shared by the incident thread and the hotel page's triage card. */
export function StepNoteModal({
  action,
  byHand = false,
  onSubmit,
  onClose,
  pending,
}: {
  action: FixStepAction;
  /**
   * `complete` on an AUTOMATIC step — the by-hand route (backend issue 50):
   * the operator made, in a console, a change this system either cannot make
   * on this hotel or has no repair built for. It asks for more than the
   * ordinary completion note because this note is the ONLY record of what
   * actually changed — on a machine execution the executor writes its own.
   */
  byHand?: boolean;
  onSubmit: (note: string) => void;
  onClose: () => void;
  pending?: boolean;
}) {
  const [note, setNote] = useState("");
  const labels: Record<string, { title: string; hint: string }> = {
    issue: {
      title: "Issue step",
      hint: "The paired blocked-on note — who was instructed, awaiting what.",
    },
    complete: {
      title: byHand ? "Record what you changed" : "Complete step",
      // Never ask "who confirmed" — the acting operator is stamped on the
      // step automatically; asking again reads as needing a second person.
      hint: byHand ? RECORD_BY_HAND_PROMPT : "What was done.",
    },
    skip: { title: "Skip step", hint: "Why this step is being skipped." },
  };
  const copy = labels[action] ?? { title: `${action} step`, hint: "" };
  return (
    <ActionModal
      title={copy.title}
      submitLabel={copy.title}
      onSubmit={() => onSubmit(note.trim())}
      onClose={onClose}
      pending={pending}
      disabled={!note.trim()}
    >
      <div>
        <label className={FIELD_LABEL_CLASS}>Note (required)</label>
        <textarea
          className={`${INPUT_CLASS} min-h-[80px]`}
          value={note}
          onChange={(e) => setNote(e.target.value)}
          placeholder={copy.hint}
          autoFocus
        />
      </div>
    </ActionModal>
  );
}
